import React from 'react';
import { useNavigate } from 'react-router-dom';
import AppPagination from '../../../components/AppPagination';
import AppActionMenu from '../../../components/AppActionMenu';
import ToolsButton from '../../../components/buttons/ToolsButton';
import { useSubCasteListScreen } from './hooks/useSubCasteListScreen';

export default function SubCasteListScreen() {
  const { state, actions } = useSubCasteListScreen();
  const navigate = useNavigate();

  const statusTabs = [
    { key: 'active', label: 'Active' },
    { key: 'archived', label: 'Archived' },
    { key: 'all', label: 'All' },
  ];

  const toolsOptions = [
    { key: 'download', label: 'Download Excel', icon: 'download' },
  ];

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-12">
      {/* Header Banner */}
      <section className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold text-on-surface-variant mb-1">
            <span>Demographics</span>
            <span className="material-symbols-outlined text-[12px]">chevron_right</span>
            <span className="text-primary font-bold">Sub-Caste Registry</span>
          </div>
          <h1 className="font-display text-4xl font-extrabold leading-[0.98] tracking-[-0.02em] text-on-surface">
            Sub-Caste Master Registry
          </h1>
          <p className="mt-1 text-xs text-on-surface-variant font-medium">
            Manage sub-caste demographic classifications and student quotas.
          </p>
        </div>

        {/* Actions Cluster: Segmented Status Tabs (Utilizes global top navbar '+ Create New' button) */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1.5 p-1 rounded-2xl bg-surface-container-high w-fit shadow-xs">
            {statusTabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => actions.setViewMode(tab.key)}
                className={`px-5 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer select-none ${
                  state.viewMode === tab.key
                    ? 'bg-primary text-on-primary shadow-sm'
                    : 'text-on-surface-variant hover:text-on-surface hover:bg-surface-container-highest/60'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Filter Row: Full Searchbar, Reset Filter, and Tools Button */}
      <section className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3 w-full max-w-md">
          <label className="relative w-full">
            <span className="material-symbols-outlined pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-outline text-lg">
              search
            </span>
            <input
              type="text"
              value={state.searchText}
              onChange={(event) => actions.setSearchText(event.target.value)}
              placeholder="Search by sub-caste name..."
              className="w-full rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-4 py-2.5 pl-10 text-xs text-on-surface outline-none transition focus:border-primary focus:ring-1 focus:ring-primary/20 shadow-2xs"
            />
          </label>

          {state.searchText && (
            <button
              type="button"
              onClick={actions.resetFilters}
              className="inline-flex items-center gap-1 shrink-0 rounded-xl bg-surface-container px-3.5 py-2.5 text-xs font-bold text-on-surface-variant hover:bg-surface-container-high transition cursor-pointer"
            >
              <span className="material-symbols-outlined text-sm">filter_alt_off</span>
              <span>Clear</span>
            </button>
          )}
        </div>

        {/* Tools Dropdown Button */}
        <div className="flex items-center gap-3 shrink-0">
          <ToolsButton
            tools={toolsOptions}
            onSelect={actions.handleToolSelect}
            disabled={state.isExporting}
            triggerClassName={`h-10 px-4 bg-surface-container-highest hover:bg-outline-variant/40 text-on-surface-variant font-bold rounded-xl text-xs flex items-center gap-2 cursor-pointer transition select-none shadow-2xs ${
              state.isExporting ? 'opacity-60 pointer-events-none' : ''
            }`}
          />
        </div>
      </section>

      {state.error && (
        <div className="rounded-xl border border-error/30 bg-error-container/20 px-4 py-3 text-xs text-error font-medium">
          {state.error}
        </div>
      )}

      {/* Main Data Table */}
      <section className="overflow-hidden rounded-2xl border border-outline-variant/25 bg-surface-container-lowest shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="border-b border-outline-variant/20 bg-surface-container-lowest text-[11px] font-bold uppercase tracking-wider text-on-surface-variant">
              <tr>
                <th className="py-3.5 pl-6 pr-4">Sub-Caste Name</th>
                <th className="px-4 py-3.5">Status</th>
                <th className="py-3.5 pl-4 pr-6 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant/10 text-on-surface">
              {state.isLoading ? (
                <tr>
                  <td colSpan={3} className="px-6 py-12 text-center text-on-surface-variant">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                      <span className="text-xs font-medium">Loading sub-castes...</span>
                    </div>
                  </td>
                </tr>
              ) : state.rows.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-6 py-12 text-center text-on-surface-variant">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <span className="material-symbols-outlined text-3xl text-outline-variant">diversity_2</span>
                      <p className="text-sm font-semibold">No sub-castes found</p>
                      <p className="text-xs text-on-surface-variant max-w-sm">
                        {state.searchText
                          ? 'No sub-castes matched your search query. Try clearing the filter.'
                          : 'No sub-castes are registered yet in this view.'}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                state.rows.map((row) => (
                  <tr
                    key={row.id}
                    onClick={() => navigate(`/masters/sub-caste/entry?id=${row.id}`)}
                    className="group transition-colors hover:bg-surface-container-low/40 cursor-pointer"
                  >
                    {/* Sub-Caste Name */}
                    <td className="py-4 pl-6 pr-4">
                      <div className="flex items-center gap-3">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary transition group-hover:scale-105">
                          <span className="material-symbols-outlined text-lg">diversity_2</span>
                        </div>
                        <div>
                          <p className="font-bold text-on-surface group-hover:text-primary transition-colors text-sm">
                            {row.name}
                          </p>
                          <span className="text-[11px] text-on-surface-variant">ID: #{row.id}</span>
                        </div>
                      </div>
                    </td>

                    {/* Status */}
                    <td className="px-4 py-4">
                      <span
                        className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-bold ${
                          row.status === 'Active'
                            ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
                            : 'bg-surface-container-high text-on-surface-variant'
                        }`}
                      >
                        <span
                          className={`h-1.5 w-1.5 rounded-full ${
                            row.status === 'Active' ? 'bg-emerald-500' : 'bg-outline'
                          }`}
                        />
                        {row.status}
                      </span>
                    </td>

                    {/* Actions Menu */}
                    <td className="py-3.5 pl-4 pr-6 text-right" onClick={(e) => e.stopPropagation()}>
                      <AppActionMenu
                        triggerLabel=""
                        triggerIcon="more_vert"
                        showChevron={false}
                        triggerClassName="rounded-lg p-1.5 text-outline-variant hover:bg-surface-container-low hover:text-primary transition bg-transparent cursor-pointer"
                        items={[
                          { key: 'edit', label: 'Edit Sub-Caste', icon: 'edit' },
                          {
                            key: 'archive',
                            label: row.status === 'Active' ? 'Archive Sub-Caste' : 'Restore Sub-Caste',
                            icon: row.status === 'Active' ? 'archive' : 'unarchive',
                          },
                          { key: 'delete', label: 'Delete Sub-Caste', icon: 'delete', danger: true },
                        ]}
                        onSelect={(key) => {
                          if (key === 'edit') {
                            navigate(`/masters/sub-caste/entry?id=${row.id}`);
                          } else if (key === 'archive') {
                            actions.handleArchive(row.id);
                          } else if (key === 'delete') {
                            actions.handleDelete(row.id);
                          }
                        }}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-t border-outline-variant/15 px-6 py-4 bg-surface-container-lowest">
          <p className="text-xs text-on-surface-variant">
            Showing{' '}
            <span className="font-bold text-on-surface">
              {state.totalCount === 0 ? 0 : (state.page - 1) * state.pageSize + 1}
            </span>{' '}
            to{' '}
            <span className="font-bold text-on-surface">
              {Math.min(state.page * state.pageSize, state.totalCount)}
            </span>{' '}
            of <span className="font-bold text-on-surface">{state.totalCount}</span> entries
          </p>

          <AppPagination
            page={state.page}
            totalPages={state.totalPages}
            onPageChange={actions.setPage}
            onPrevious={() => actions.setPage((p) => Math.max(p - 1, 1))}
            onNext={() => actions.setPage((p) => Math.min(p + 1, state.totalPages))}
            canPrevious={state.page > 1}
            canNext={state.page < state.totalPages}
          />
        </div>
      </section>
    </div>
  );
}
